import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import moment from 'moment';
import { adminAxiosInstance } from '../api/axios';

const ADMIN_URL = adminAxiosInstance.defaults.baseURL || 'http://localhost:5000';
// Extract just the protocol, host, and port - remove any path to avoid namespace issues
const SOCKET_URL = (() => {
  try {
    const url = new URL(ADMIN_URL);
    return `${url.protocol}//${url.host}`;
  } catch {
    // Fallback if URL parsing fails
    return 'http://localhost:5000'.replace(/\/.*$/, '');
  }
})();

export const useFileJobs = () => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [showDisconnectAlert, setShowDisconnectAlert] = useState(false);
  const [jobUpdates, setJobUpdates] = useState({});
  const [newJobs, setNewJobs] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);

  // Initialize socket connection
  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      console.log('No token found, skipping socket connection');
      return;
    }

    // Create socket connection
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: {
        token: `Bearer ${token}`,
      },
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    const socket = socketRef.current;

    // Connection handlers
    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
      setIsConnected(true);

      // Join admin room for global updates
      socket.emit('subscribe', 'admin');
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
      if (reason !== 'io server disconnect') {
        // Only show alert for unexpected disconnections
        setShowDisconnectAlert(true);
      }
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
      setShowDisconnectAlert(true);
    });

    socket.on('reconnect_attempt', (attempt) => {
      console.log('Reconnection attempt:', attempt);
      if (attempt > 1) {
        setShowDisconnectAlert(true);
      }
    });

    socket.on('reconnect', () => {
      console.log('Reconnected');
      setIsConnected(true);
      setShowDisconnectAlert(false);
    });

    // Listen for job updates
    socket.on('job:created', (data) => {
      console.log('Job created:', data);
      setJobUpdates(prev => ({
        ...prev,
        [data.jobId]: data,
      }));
      setNewJobs(prev => [data, ...prev]);
      setRecentJobs(prev => [data, ...prev.slice(0, 9)]);
    });

    socket.on('job:update', (data) => {
      console.log('Job update:', data);
      setJobUpdates(prev => ({
        ...prev,
        [data.jobId]: {
          ...prev[data.jobId],
          ...data,
        },
      }));
    });

    socket.on('job:completed', (data) => {
      console.log('Job completed:', data);
      setJobUpdates(prev => ({
        ...prev,
        [data.jobId]: {
          ...prev[data.jobId],
          ...data,
          status: 'completed',
        },
      }));
    });

    socket.on('job:failed', (data) => {
      console.log('Job failed:', data);
      setJobUpdates(prev => ({
        ...prev,
        [data.jobId]: {
          ...prev[data.jobId],
          ...data,
          status: 'failed',
        },
      }));
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  // Subscribe to specific job updates
  const subscribeToJob = useCallback((jobId) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('subscribe', `job_${jobId}`);
    }
  }, [isConnected]);

  // Unsubscribe from specific job
  const unsubscribeFromJob = useCallback((jobId) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('unsubscribe', `job_${jobId}`);
    }
  }, [isConnected]);

  // Fetch all jobs from API
  const fetchJobs = useCallback(async (options = {}) => {
    try {
      const { page = 1, limit = 20, status, fileType } = options;
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (status) params.append('status', status);
      if (fileType) params.append('fileType', fileType);

      const response = await adminAxiosInstance.get(`/file-jobs?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw error;
    }
  }, []);

  // Fetch single job status
  const fetchJobStatus = useCallback(async (jobId) => {
    try {
      const response = await adminAxiosInstance.get(`/file-jobs/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching job status:', error);
      throw error;
    }
  }, []);

  // Create new file job
  const createFileJob = useCallback(async (jobData) => {
    try {
      const response = await adminAxiosInstance.post('/file-jobs', jobData);
      
      // Subscribe to this job's updates
      if (response.data.data?.jobId) {
        subscribeToJob(response.data.data.jobId);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error creating file job:', error);
      throw error;
    }
  }, [subscribeToJob]);

  // Download file - backend streams file directly
  const downloadFile = useCallback(async (jobId) => {
    try {
      // Get job details for proper filename
      const jobResponse = await adminAxiosInstance.get(`/file-jobs/${jobId}`);
      const jobData = jobResponse.data.data;
      const fileType = jobData.fileType;
      const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const extension = 'csv'//fileType === 'report' ? 'json' : 'csv';
      const filename = `${fileType}_${date}.${extension}`;

      const response = await adminAxiosInstance.get(`/file-jobs/${jobId}/download`, {
        responseType: 'blob', // Triggers browser download
      });

      // Check if server already provided a CSV file
      const isAlreadyCsv = jobData.outputFileName && jobData.outputFileName.endsWith('.csv');

      // For report files, also parse and convert to CSV
      if (fileType.startsWith('report')) {
        try {
          // Convert blob to text to parse JSON
          const text = await response.data.text();
          const jsonData = JSON.parse(text);

          if (jsonData.data && jsonData.data.processed && jsonData.data.unprocessed) {
            const { processed, unprocessed } = jsonData.data;

            // Convert processed array to CSV
            if (processed && processed.length > 0) {
              const processedCsv = convertToCSV(processed);
              downloadCSV(processedCsv, `${filename.replace('.json', '').replace('.csv','')}_correct.csv`);
            }

            // Convert unprocessed array to CSV
            if (unprocessed && unprocessed.length > 0) {
              const unprocessedCsv = convertToCSV(unprocessed);
              downloadCSV(unprocessedCsv, `${filename.replace('.json', '').replace('.csv','')}_incorrect.csv`);
            }
          }
        } catch (parseError) {
          console.error('Error parsing report JSON:', parseError);
        }
      } else if (fileType === 'transaction_csv') {
        // If it's already a CSV from the server, just download it directly
        if (isAlreadyCsv) {
          const csvContent = await response.data.text();
          downloadCSV(csvContent, filename);
          return;
        }

        // For transaction CSV files, parse JSON and transform using AdminTransactions logic
        try {
          const text = await response.data.text();
          const jsonData = JSON.parse(text);

          if (jsonData.results && Array.isArray(jsonData.results)) {
            // Transform data using same logic as AdminTransactions
            const transformedData = transformer(jsonData.results);

            // Generate CSV using the same logic
            const csvContent = convertToCSV(transformedData);

            // Download as CSV
            downloadCSV(csvContent, filename);
          } else {
            console.error('Invalid transaction data format');
          }
        } catch (parseError) {
          console.error('Error parsing transaction JSON:', parseError);
        }
      } else if (fileType === 'leads_csv') {
        // If it's already a CSV from the server, just download it directly
        if (isAlreadyCsv) {
          const csvContent = await response.data.text();
          downloadCSV(csvContent, filename);
          return;
        }

        // For leads CSV files, parse JSON and transform using AdminLeadsHistory logic
        try {
          const text = await response.data.text();
          const jsonData = JSON.parse(text);
          console.log({jsonData})

          if (jsonData.results && Array.isArray(jsonData.results)) {
            // Transform data using same logic as AdminLeadsHistory parse_leads function
            const transformedData = leadsTransformer(jsonData.results);

            // Generate CSV using the same logic
            const csvContent = convertToCSV(transformedData);

            // Download as CSV
            downloadCSV(csvContent, filename);
          } else {
            console.error('Invalid leads data format');
          }
        } catch (parseError) {
          console.error('Error parsing leads JSON:', parseError);
        }
      } else {
        // Default download for other types
        const text = await response.data.text();
        downloadCSV(text, filename);
      }

    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }, []);

  // Get job statistics
  const getJobStats = useCallback(async () => {
    try {
      const response = await adminAxiosInstance.get('/file-jobs/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching job stats:', error);
      throw error;
    }
  }, []);

  // Delete a job
  const deleteJob = useCallback(async (jobId) => {
    try {
      const response = await adminAxiosInstance.delete(`/file-jobs/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting job:', error);
      throw error;
    }
  }, []);

  // Helper function to convert array of objects to CSV
  // Fields stored already IST-shifted (via adjustedMoment) -> format as-is; every
  // other date column is true UTC and gets +5:30 to show IST wall-clock.
  const preShiftedDateKeys = ['last_updated_date', 'date_created'];
  // Match a JS Date, an ISO-8601 string, or a JS Date.toString() string like
  // "Tue Jun 23 2026 01:10:02 GMT+0530 (India Standard Time)".
  const isDateLike = (v) => {
    if (v instanceof Date) return true;
    if (typeof v !== 'string') return false;
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v) ||
      /^[A-Za-z]{3} [A-Za-z]{3} \d{2} \d{4} \d{2}:\d{2}:\d{2} GMT/.test(v);
  };
  const formatHumanDate = (v, key) => {
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d.getTime())) return typeof v === 'string' ? v : '';
    const m = preShiftedDateKeys.includes(key)
      ? moment.utc(d)
      : moment.utc(d).add(330, 'minutes');
    return m.format('DD MMM YY, HH:mm');
  };

  const convertToCSV = (data) => {
    if (!data || !data.length) return '';

    const headers = Object.keys(data[0]);
    const csvRows = [];

    // Add headers
    csvRows.push(headers.join(','));

    // Add data rows
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        // Render date/datetime columns as human-readable "Day Month YY, hour:minute"
        if (isDateLike(value)) {
          return formatHumanDate(value, header);
        }
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        const stringValue = String(value || '');
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  };

  const transformer = (transaction) => {
    let temp = [];
    console.log(transaction);
    try {
      for (let i of transaction) {
        let obj = {
          ...i,
          action:
            i?.action && typeof i?.action === "object"
              ? i.action.join(" - ")
              : "",
          currentLevel: i?.lead?.currentLevel,
          source: i?.source,
          paid: i?.lead?.paid,
          amount: i?.amount,
          lead_id: i?.lead?._id,
          lead_createdAt: i?.lead?.createdAt,
          lead_updatedAt: i?.lead?.updatedAt,
          due_payment: i?.lead?.due_payment,
          customerName: i?.lead?.parameters && i?.lead?.parameters?.name,
          customerNumber: i?.lead?.parameters && i?.lead?.parameters?.number,
          status: i?.campaign?.status,
          title: i?.campaign?.title,
          name: i?.user?.name,
          user_id: i?.user?.user_id,
          bankAccName: `${i?.user?.bankAccount?.account_name || "-"}`,
          bankAccNumber: `${i?.user?.bankAccount?.account_number || "-"}`,
          bankIFSC: `${String(i?.user?.bankAccount?.ifsc_code) || "-"}`,
          bankValid: `${String(i?.user?.bankAccount?.valid) || "-"}`,
          balance: i?.user?.wallet ? i?.user?.wallet?.totalBalance : "-",
        };
        delete obj["campaign"];
        delete obj["user"];
        delete obj["lead"];
        temp.push(obj);
      }
    } catch (e) {
      console.log(e);
    }
    return temp;
  };

  const leadsTransformer = (lead) => {
    let temp_obj = JSON.parse(JSON.stringify(lead));

    temp_obj = temp_obj.map((i) => {
      i["campaign"] = i["product_id"]["title"];
      delete i["product_id"];
      delete i["__v"];
      delete ["date_created"];

      delete i["redirect_url"];
      delete i["expiry"];
      delete i["clicked"];
      delete i["lead_url"];
      i["_id"] = i?._id || i?.id || "";
      i["sub_lead_id"] = i?.sub_lead_id || "";
      i["EP_id"] = i?.user_id?.user_id || "";
      i["name"] = i?.parameters?.name || i?.parameters?.Name
      i["number"] = i?.parameters?.number || i?.parameters?.Number;
      i["Area Pincode"] = i?.parameters?.["Area Pincode"];
      i["earnType"] = i["earntype"];
      i["currentLevel"] = typeof i?.currentLevel === "number" ? i.currentLevel : 0;
      // Action Header + Action Message as separate columns (mirrors the table).
      const targetLevel = i?.currentLevel || 1;
      const levelMeta = (i?.levelMeta || []).filter(
        (levels) => levels.level === targetLevel
      );
      if (levelMeta.length) {
        i["Action Header"] = levelMeta[0].action_header || i?.action_header || "-";
        i["Action Message"] = `title: ${levelMeta[0].title || ""} | description: ${levelMeta[0].description || ""}`;
      } else {
        i["Action Header"] = i?.action_header || "-";
        i["Action Message"] = i?.action_message || "-";
      }
      delete i["parameters"];
      delete i["user_id"];
      delete i["levelMeta"];
      delete i["earntype"];

      return i;
    });
    return temp_obj;
  };

  // Helper function to download CSV content
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return {
    isConnected,
    showDisconnectAlert,
    jobUpdates,
    newJobs,
    recentJobs,
    subscribeToJob,
    unsubscribeFromJob,
    fetchJobs,
    fetchJobStatus,
    createFileJob,
    downloadFile,
    getJobStats,
    deleteJob,
  };
};

export default useFileJobs;
